import { Link } from 'react-router-dom';
import type { Article } from '@/utils/articles';
import { timeAgo, formatViews } from '@/utils/format';

interface ArticleCardProps {
  article: Article;
  variant?: 'grid' | 'horizontal' | 'compact';
  rank?: number;
}

export default function ArticleCard({ article, variant = 'grid', rank }: ArticleCardProps) {
  const detailUrl = `/detail/${article.id}`;

  if (variant === 'compact') {
    return (
      <Link
        to={detailUrl}
        className="group flex items-start gap-3 py-3 border-b border-background-200 last:border-b-0"
      >
        {typeof rank === 'number' && (
          <span
            className={`shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold font-label ${
              rank <= 3 ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-600'
            }`}
          >
            {rank}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {timeAgo(article.publishTime)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={detailUrl}
        className="group flex gap-4 bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors"
      >
        <div className="w-28 sm:w-40 h-28 sm:h-32 shrink-0 bg-background-100 overflow-hidden">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title}-91黑料娱乐爆料`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0 py-3 pr-4 flex flex-col">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-primary-100 text-primary-700">
              {article.categoryName}
            </span>
            {article.isHot && (
              <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-accent-100 text-accent-800 flex items-center gap-0.5">
                <i className="ri-fire-line"></i> 热门
              </span>
            )}
          </div>
          <h3 className="text-sm sm:text-base font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="hidden sm:block text-sm text-foreground-600 mt-1.5 line-clamp-2 flex-1">{article.excerpt}</p>
          <div className="flex items-center gap-4 mt-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {timeAgo(article.publishTime)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={detailUrl}
      className="group flex flex-col bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors h-full"
    >
      <div className="w-full aspect-[4/3] bg-background-100 overflow-hidden relative">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title}-91黑料娱乐爆料`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md text-[11px] font-medium bg-foreground-950/80 text-background-50 backdrop-blur-sm">
          {article.categoryName}
        </span>
        {article.isHot && (
          <span className="absolute top-3 right-3 px-2 py-1 rounded-md text-[11px] font-medium bg-primary-500 text-background-50 flex items-center gap-0.5">
            <i className="ri-fire-line"></i> 爆
          </span>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="text-base font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 mt-2 line-clamp-2 flex-1">{article.excerpt}</p>
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-background-200 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {timeAgo(article.publishTime)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatViews(article.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}